// Ask HR: history on the left, the conversation on the right. Answers stream in with numbered
// links to the page they came from. "/" is a new chat; "/c/<id>" is a saved one.

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { api } from "../api.js";
import { navigate } from "../router.js";
import { AnswerText, SourceList } from "./Answer.js";
import { postChat, type Audience, type LocationSummary, type Source } from "./client.js";
import { LocationAsk, LocationPicker } from "./Location.js";
import { TalkToHr } from "./TalkToHr.js";
import { STATUS_LABELS, CATEGORIES, type Category, type EscalationStatus } from "@ask-hr/core/escalations";

interface Conversation {
  id: string;
  title: string;
  updatedAt: string;
}

interface StoredMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  sources?: Source[];
  feedback?: "up" | "down";
}

interface MyRequest {
  id: string;
  status: EscalationStatus;
  category: Category;
  createdAt: string;
}

interface UiMessage {
  key: string;
  id?: string;
  role: "user" | "assistant";
  text: string;
  sources: Source[];
  pending?: boolean;
  error?: string;
  feedback?: "up" | "down";
}

const SUGGESTIONS = [
  "How many paid holidays do we get?",
  "What's the daily meal limit when I travel?",
  "How does PTO accrue?",
  "How does tuition reimbursement work?",
];

export function conversationIdOf(path: string): string | null {
  const m = /^\/c\/([0-9a-z]{9,40})$/.exec(path);
  return m?.[1] ?? null;
}

const relative = (iso: string) => {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString();
};

export function ChatPage({ path, firstName }: { path: string; firstName: string }) {
  const routeId = conversationIdOf(path);
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(routeId);
  const [location, setLocation] = useState<LocationSummary | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [talkFor, setTalkFor] = useState<string | null>(null);
  // A question held until the asker says where they work.
  const [held, setHeld] = useState<{ question: string; hr: Audience | null } | null>(null);
  const [requests, setRequests] = useState<MyRequest[]>([]);
  const streamingInto = useRef<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const nextKey = useRef(0);

  const loadRequests = () =>
    api<{ requests: MyRequest[] }>("GET", "/api/escalations").then(
      (r) => setRequests(r.requests),
      () => {},
    );

  const loadConversations = () =>
    api<{ conversations: Conversation[] }>("GET", "/api/conversations").then(
      (r) => setConversations(r.conversations),
      () => setConversations([]),
    );

  useEffect(() => {
    let live = true;
    api<{ conversations: Conversation[] }>("GET", "/api/conversations").then(
      (r) => live && setConversations(r.conversations),
      () => live && setConversations([]),
    );
    api<{ requests: MyRequest[] }>("GET", "/api/escalations").then(
      (r) => live && setRequests(r.requests),
      () => {},
    );
    api<{ location: LocationSummary }>("GET", "/api/me").then(
      (r) => live && setLocation(r.location),
      () => {},
    );
    return () => {
      live = false;
    };
  }, []);

  // Open a saved conversation when the path changes (not the one being streamed into).
  useEffect(() => {
    if (routeId && routeId === streamingInto.current) return;
    let live = true;
    if (!routeId) {
      queueMicrotask(() => {
        if (!live) return;
        setMessages([]);
        setConversationId(null);
        setLoadError(false);
      });
      return () => {
        live = false;
      };
    }
    api<{ messages: StoredMessage[] }>("GET", `/api/conversations/${routeId}`).then(
      (r) => {
        if (!live) return;
        setConversationId(routeId);
        setLoadError(false);
        setMessages(
          r.messages.map((m) => ({
            key: m.id,
            id: m.id,
            role: m.role,
            text: m.text,
            sources: m.sources ?? [],
            ...(m.feedback ? { feedback: m.feedback } : {}),
          })),
        );
      },
      () => live && setLoadError(true),
    );
    return () => {
      live = false;
    };
  }, [routeId]);

  // Follow a streaming answer only if the reader is already at the bottom. Scrolling up to read
  // earlier messages must never be yanked back down by the next chunk of text.
  const following = useRef(true);
  useEffect(() => {
    const onScroll = () => {
      const fromBottom = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
      following.current = fromBottom < 160;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    if (following.current) endRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const patchLast = (fn: (m: UiMessage) => UiMessage) =>
    setMessages((ms) => ms.map((m, i) => (i === ms.length - 1 ? fn(m) : m)));

  async function send(text: string) {
    const question = text.trim();
    if (!question || busy) return;
    setBusy(true);
    setDraft("");
    following.current = true; // asking a question jumps to the new answer
    const stamp = String((nextKey.current += 1));
    setMessages((ms) => [
      ...ms,
      { key: `u${stamp}`, role: "user", text: question, sources: [] },
      { key: `a${stamp}`, role: "assistant", text: "", sources: [], pending: true },
    ]);
    try {
      await postChat({ ...(conversationId ? { conversationId } : {}), message: question }, (line) => {
        if (line.t === "meta") {
          setLocation(line.location);
          if (!conversationId) {
            streamingInto.current = line.conversationId;
            setConversationId(line.conversationId);
            navigate(`/c/${line.conversationId}`);
          }
        } else if (line.t === "text") {
          patchLast((m) => ({ ...m, text: m.text + line.v }));
        } else if (line.t === "source") {
          patchLast((m) => ({ ...m, sources: [...m.sources, line.source] }));
        } else if (line.t === "needs_location") {
          // Nothing was answered or saved: take the question back off the screen and ask first.
          setMessages((ms) => ms.slice(0, -2));
          setHeld({ question: line.question, hr: line.location.hr });
          setLocation(line.location);
        } else if (line.t === "done") {
          patchLast((m) => ({ ...m, id: line.messageId, pending: false }));
        } else if (line.t === "error") {
          patchLast((m) => ({ ...m, pending: false, error: line.message }));
        }
      });
    } catch {
      patchLast((m) => ({ ...m, pending: false, error: "Couldn't reach Ask HR. Check your connection and try again." }));
    } finally {
      patchLast((m) => (m.pending ? { ...m, pending: false } : m));
      setBusy(false);
      void loadConversations();
      inputRef.current?.focus();
    }
  }

  async function rate(m: UiMessage, rating: "up" | "down") {
    if (!m.id || !conversationId) return;
    const next = m.feedback === rating ? null : rating;
    setMessages((ms) => ms.map((x) => (x.key === m.key ? { ...x, ...(next ? { feedback: next } : { feedback: undefined }) } : x)) as UiMessage[]);
    try {
      await api("PUT", `/api/conversations/${conversationId}/messages/${m.id}/feedback`, { rating: next });
    } catch {}
  }

  async function remove(id: string) {
    try {
      await api("DELETE", `/api/conversations/${id}`);
    } catch {}
    setConfirmDelete(null);
    if (id === conversationId) navigate("/");
    void loadConversations();
  }

  async function chooseLocation(audience: Audience | null) {
    try {
      setLocation((await api<{ location: LocationSummary }>("PUT", "/api/me/location", { audience })).location);
    } catch {}
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void send(draft);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send(draft);
    }
  };

  const newChat = () => {
    streamingInto.current = null;
    setHistoryOpen(false);
    navigate("/");
    inputRef.current?.focus();
  };

  return (
    <div className="chat">
      <aside className={`history${historyOpen ? " open" : ""}`} aria-label="Your conversations">
        <div className="history-head">
          <button type="button" className="primary" onClick={newChat} disabled={busy}>
            New chat
          </button>
          <button type="button" className="history-toggle" aria-expanded={historyOpen} onClick={() => setHistoryOpen(!historyOpen)}>
            History
          </button>
        </div>
        <nav className="history-list">
          {conversations === null ? (
            <p className="sub">Loading…</p>
          ) : conversations.length === 0 ? (
            <p className="sub">Your questions will appear here. They're kept for 90 days.</p>
          ) : (
            <ul>
              {conversations.map((c) => (
                <li key={c.id} className={c.id === conversationId ? "current" : undefined}>
                  {confirmDelete === c.id ? (
                    <div className="confirm">
                      <span>Delete this conversation?</span>
                      <button type="button" className="danger" onClick={() => void remove(c.id)}>
                        Delete
                      </button>
                      <button type="button" onClick={() => setConfirmDelete(null)}>
                        Keep
                      </button>
                    </div>
                  ) : (
                    <>
                      <a
                        href={`/c/${c.id}`}
                        aria-current={c.id === conversationId ? "page" : undefined}
                        onClick={(e) => {
                          e.preventDefault();
                          if (busy) return;
                          streamingInto.current = null;
                          setHistoryOpen(false);
                          navigate(`/c/${c.id}`);
                        }}
                      >
                        <span className="title">{c.title}</span>
                        <span className="when">{relative(c.updatedAt)}</span>
                      </a>
                      <button type="button" className="icon" aria-label={`Delete "${c.title}"`} onClick={() => setConfirmDelete(c.id)}>
                        ×
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </nav>
        {requests.length ? (
          <div className="my-requests">
            <h3>Your HR requests</h3>
            <ul>
              {requests.slice(0, 8).map((r) => (
                <li key={r.id}>
                  <span>{CATEGORIES[r.category]}</span>
                  <span className={r.status === "resolved" ? "pill ok" : r.status === "new" ? "pill alert" : "pill"}>{STATUS_LABELS[r.status]}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <button type="button" className="talk-general" onClick={() => setTalkFor("general")}>
          Talk to HR
        </button>
      </aside>

      <section className="conversation" aria-label="Conversation">
        <div className="messages" aria-busy={busy}>
          {loadError ? (
            <p className="notice" role="alert">
              That conversation couldn't be opened. It may have been deleted, or it's older than 90 days.
            </p>
          ) : null}
          {messages.length === 0 && !loadError ? (
            <div className="welcome">
              <h2>Hi {firstName}, what can I help you find?</h2>
              <p className="sub">
                Ask about Novara HR policy: time off, holidays, benefits, travel and expenses, and more. Every answer links to
                the HR document it came from.
              </p>
              <div className="suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => void send(s)} disabled={busy}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) =>
              m.role === "user" ? (
                <div key={m.key} className="msg mine">
                  <p>{m.text}</p>
                </div>
              ) : (
                <div key={m.key} className="msg bot">
                  {m.text ? <AnswerText text={m.text} sources={m.sources} /> : m.pending ? <p className="typing">Looking through the HR documents…</p> : null}
                  {m.error ? (
                    <p className="notice" role="alert">
                      {m.error}
                    </p>
                  ) : null}
                  {!m.pending ? <SourceList sources={m.sources} /> : null}
                  {m.id && !m.pending && !m.error ? (
                    <div className="rate" role="group" aria-label="Was this helpful?">
                      <button type="button" className="icon" aria-pressed={m.feedback === "up"} aria-label="Helpful" onClick={() => void rate(m, "up")}>
                        👍
                      </button>
                      <button type="button" className="icon" aria-pressed={m.feedback === "down"} aria-label="Not helpful" onClick={() => void rate(m, "down")}>
                        👎
                      </button>
                      <button type="button" className="talk-btn" onClick={() => setTalkFor(m.key)}>
                        Talk to HR
                      </button>
                    </div>
                  ) : null}
                  {talkFor === m.key ? (
                    <TalkToHr conversationId={conversationId} onClose={() => setTalkFor(null)} onSent={() => void loadRequests()} />
                  ) : null}
                </div>
              ),
            )
          )}
          {talkFor === "general" ? (
            <TalkToHr conversationId={conversationId} onClose={() => setTalkFor(null)} onSent={() => void loadRequests()} />
          ) : null}
          <div ref={endRef} />
        </div>

        {held ? (
          <LocationAsk
            question={held.question}
            hr={held.hr}
            onChoose={(a) => {
              const q = held.question;
              setHeld(null);
              void chooseLocation(a).then(() => send(q));
            }}
          />
        ) : location && location.audience === null ? (
          <LocationAsk onChoose={(a) => void chooseLocation(a)} />
        ) : null}
        <form className="composer" onSubmit={onSubmit}>
          <label className="sr-only" htmlFor="question">
            Your question
          </label>
          <textarea
            id="question"
            ref={inputRef}
            rows={2}
            maxLength={4000}
            placeholder="Ask a question about Novara HR policy"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
          />
          <button type="submit" className="primary" disabled={busy || !draft.trim()}>
            {busy ? "Answering…" : "Ask"}
          </button>
        </form>
        <div className="fineprint">
          {location ? <LocationPicker location={location} onChoose={(a) => void chooseLocation(a)} /> : null}
          <span>For your own balances or pay, use Paylocity. Check the linked source for anything important.</span>
        </div>
      </section>
    </div>
  );
}
